import Swal from 'sweetalert2';

/**
 * Eskrag Custom SweetAlert2 Theme & Utility Helper
 * Provides consistent, ultra-modern, dark-mode aware alerts across the entire app.
 */

// Helper to detect current dark mode
const isDarkMode = () => {
  return document.documentElement.classList.contains('dark') || 
         document.body.classList.contains('dark') ||
         localStorage.getItem('theme') === 'dark';
};

// Base config for Eskrag popups
const getBaseConfig = () => {
  const dark = isDarkMode();
  return {
    background: dark ? '#1e1e24' : '#ffffff',
    color: dark ? '#f3f4f6' : '#111827',
    customClass: {
      popup: 'eskrag-swal-popup shadow-2xl rounded-2xl border border-gray-200 dark:border-gray-800 p-6',
      title: 'eskrag-swal-title text-xl font-black text-gray-900 dark:text-white mb-2',
      htmlContainer: 'eskrag-swal-content text-sm text-gray-600 dark:text-gray-300',
      confirmButton: 'eskrag-swal-btn eskrag-swal-confirm px-6 py-2.5 rounded-xl font-bold text-white transition-all transform active:scale-95 shadow-md',
      cancelButton: 'eskrag-swal-btn eskrag-swal-cancel px-6 py-2.5 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all active:scale-95 ml-3',
      actions: 'flex items-center justify-center gap-3 mt-6',
    },
    buttonsStyling: false,
    showClass: {
      popup: 'swal2-show animate-scale-up',
    },
    hideClass: {
      popup: 'swal2-hide animate-scale-down',
    }
  };
};

/**
 * 1. Order Processing Alert (Animated & Friendly)
 */
export const showOrderProcessing = (title = 'Processing Your Order...', text = 'Please hold on while we secure your items and update your account.') => {
  const dark = isDarkMode();
  return Swal.fire({
    ...getBaseConfig(),
    title: `<div class="flex items-center justify-center gap-2 mb-1">
              <span class="inline-block animate-bounce">⚡</span>
              <span>${title}</span>
            </div>`,
    html: `
      <div class="py-4 text-center">
        <div class="relative w-16 h-16 mx-auto mb-4">
          <div class="absolute inset-0 rounded-full border-4 border-indigo-500/20 dark:border-indigo-400/20"></div>
          <div class="w-16 h-16 rounded-full border-4 border-indigo-600 dark:border-indigo-400 border-t-transparent animate-spin"></div>
          <div class="absolute inset-0 flex items-center justify-center text-lg font-black text-indigo-600 dark:text-indigo-400">
            🛒
          </div>
        </div>
        <p class="text-sm font-medium text-gray-600 dark:text-gray-300 leading-relaxed">${text}</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-2">Please do not close or refresh this page.</p>
      </div>
    `,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
};

/**
 * Close any active SweetAlert
 */
export const closeAlert = () => {
  Swal.close();
};

/**
 * 2. Cancel Order Confirmation Modal
 */
export const confirmCancelOrder = async (orderId) => {
  const result = await Swal.fire({
    ...getBaseConfig(),
    icon: 'warning',
    iconColor: '#f59e0b',
    title: 'Cancel This Order?',
    html: `
      <div class="py-2 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Are you sure you want to cancel order <span class="font-bold text-indigo-600 dark:text-indigo-400 font-mono bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded">#ESK-${orderId}</span>?
        </p>
        <p class="text-xs text-amber-600 dark:text-amber-400 mt-3 bg-amber-50 dark:bg-amber-950/30 p-2.5 rounded-lg border border-amber-200 dark:border-amber-900/50 flex items-center justify-center gap-1.5">
          <span>⚠️</span> Any balance deducted for this order will be refunded back to your account wallet immediately.
        </p>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Yes, Cancel Order',
    cancelButtonText: 'Keep Order',
    customClass: {
      ...getBaseConfig().customClass,
      confirmButton: 'px-5 py-2.5 rounded-xl font-bold text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-500/20 transition-all active:scale-95',
    }
  });

  return result.isConfirmed;
};

/**
 * 3. Order Cancelled Success Modal
 */
export const showOrderCancelled = (orderId) => {
  return Swal.fire({
    ...getBaseConfig(),
    icon: 'success',
    iconColor: '#10b981',
    title: 'Order Cancelled Successfully',
    html: `
      <div class="py-2 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Order <span class="font-bold font-mono">#ESK-${orderId}</span> has been cancelled.
        </p>
        <p class="text-xs text-emerald-600 dark:text-emerald-400 mt-2 font-medium">
          ✅ Your wallet balance has been credited back.
        </p>
      </div>
    `,
    timer: 3500,
    timerProgressBar: true,
    showConfirmButton: false,
  });
};

/**
 * 4. Welcome Back Toast/Popup
 */
export const showWelcomeBack = (userName = '') => {
  const cleanName = userName && userName !== 'Guest' ? userName : '';
  const greeting = cleanName ? `Welcome back, ${cleanName}! 👋` : 'Welcome back! 👋';
  
  return Swal.fire({
    ...getBaseConfig(),
    icon: 'success',
    iconColor: '#6366f1',
    title: greeting,
    html: `
      <div class="py-1 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-300">
          It's great to see you again! Your account is ready.
        </p>
      </div>
    `,
    timer: 2500,
    timerProgressBar: true,
    showConfirmButton: false,
    position: 'top',
    toast: false,
    customClass: {
      ...getBaseConfig().customClass,
      popup: 'eskrag-swal-popup shadow-2xl rounded-2xl border border-indigo-200 dark:border-indigo-900 p-5 mt-6',
    }
  });
};

/**
 * 5. Logging Out (Warm & Friendly "See you soon")
 */
export const showLoggingOut = (userName = '') => {
  const cleanName = userName && userName !== 'Guest' ? userName : '';
  const farewell = cleanName ? `See you soon, ${cleanName}! 👋` : 'See you soon! 👋';

  return Swal.fire({
    ...getBaseConfig(),
    title: farewell,
    html: `
      <div class="py-3 text-center">
        <div class="text-3xl mb-2 animate-bounce">👋</div>
        <p class="text-sm text-gray-600 dark:text-gray-300 font-medium">
          Logging you out safely... Thanks for visiting Eskrag!
        </p>
        <div class="w-24 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mx-auto mt-4 animate-pulse"></div>
      </div>
    `,
    timer: 1800,
    timerProgressBar: true,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
};

/**
 * 6. Confirm Logout Prompt
 */
export const confirmLogout = async () => {
  const result = await Swal.fire({
    ...getBaseConfig(),
    title: 'Ready to Sign Out? 👋',
    html: `
      <div class="py-2 text-center text-sm text-gray-600 dark:text-gray-300">
        Are you sure you want to log out of your Eskrag account?
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Yes, Sign Out',
    cancelButtonText: 'Stay Logged In',
    customClass: {
      ...getBaseConfig().customClass,
      confirmButton: 'px-5 py-2.5 rounded-xl font-bold text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-500/20 transition-all active:scale-95',
    }
  });

  return result.isConfirmed;
};

/**
 * 7. Token Expired / Session Expired Alert
 */
export const showSessionExpired = () => {
  return Swal.fire({
    ...getBaseConfig(),
    icon: 'info',
    iconColor: '#6366f1',
    title: 'Session Expired 🔒',
    html: `
      <div class="py-2 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Your security session has expired. Please sign in again to access your account and continue your orders.
        </p>
      </div>
    `,
    confirmButtonText: 'Sign In Again',
    allowOutsideClick: false,
    allowEscapeKey: false,
    customClass: {
      ...getBaseConfig().customClass,
      confirmButton: 'px-6 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/30 transition-all active:scale-95',
    }
  });
};

/**
 * Quick Toast Notifications
 */
export const showToast = ({ icon = 'success', title = '', timer = 3000 }) => {
  const dark = isDarkMode();
  return Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title,
    showConfirmButton: false,
    timer,
    timerProgressBar: true,
    background: dark ? '#1e1e24' : '#ffffff',
    color: dark ? '#f3f4f6' : '#111827',
    customClass: {
      popup: 'rounded-xl shadow-xl border border-gray-200 dark:border-gray-800 text-sm font-semibold',
    }
  });
};

export default {
  showOrderProcessing,
  closeAlert,
  confirmCancelOrder,
  showOrderCancelled,
  showWelcomeBack,
  showLoggingOut,
  confirmLogout,
  showSessionExpired,
  showToast,
};
